import _ from 'lodash'
import * as Lib from '../constants/annotator'

// Half-window (in seconds) searched on each side of a click when snapping a
// beat marking to a local peak. 40 samples at 300 Hz in Turk V1; expressed here
// in seconds so the pixel-time search radius stays constant across sampling
// rates.
export const PEAK_SEARCH_HALF_WINDOW_SEC = 40 / 300

const square = (n) => n * n

// Given a click at (sampleIndex, yAmp) on a sample series, return the sample
// within a time-bounded window that minimizes pixel-space distance to the
// click. Amplitude and time are both weighted in *rendered pixels* — that's
// why PX_PER_MV and PX_PER_SECOND appear in the metric.
//
// `halfWindowSec` bounds the search radius. The default suits R-peaks, which
// are the tallest thing nearby. Narrow it for low-amplitude or very thin
// features (P-wave peaks, pace spikes): because the metric weights 1 mV of
// amplitude error (40 px) far more heavily than the entire default time span
// (13.3 px), a tall neighbour anywhere in the window will otherwise win — a
// paced QRS immediately following a ventricular pace spike being the worst case.
export const searchForLocalPeak = (sampleIndex, yAmp, samples, samplesPerSecond, halfWindowSec = PEAK_SEARCH_HALF_WINDOW_SEC) => {
    // Defensive defaults: these values can occur before data is loaded / lead is selected.
    if (!Array.isArray(samples) || samples.length === 0 || !Number.isFinite(samplesPerSecond) || samplesPerSecond <= 0) {
        return { index: 0, sampleData: 0 }
    }

    const safeSampleIndex = Number.isFinite(sampleIndex) ? Math.round(sampleIndex) : 0
    const clampedSampleIndex = _.clamp(safeSampleIndex, 0, samples.length - 1)
    const safeYAmp = Number.isFinite(yAmp) ? yAmp : 0
    const safeHalfWindowSec = Number.isFinite(halfWindowSec) && halfWindowSec > 0
        ? halfWindowSec
        : PEAK_SEARCH_HALF_WINDOW_SEC

    // At least 1 sample each side, so a tight window on a low-rate recording
    // still allows a one-sample correction instead of pinning the raw click.
    const windowWidth = Math.max(1, Math.round(samplesPerSecond * safeHalfWindowSec))
    const lastIndex = samples.length - 1
    const startIndex = _.clamp(clampedSampleIndex - windowWidth, 0, lastIndex)
    const endIndex = _.clamp(clampedSampleIndex + windowWidth, 0, lastIndex)

    let minDiffVal2
    let minDiffIndex = startIndex
    for (let i = startIndex; i <= endIndex; i++) {
        const dy = Lib.PX_PER_MV * (safeYAmp - samples[i])
        const dx = (Lib.PX_PER_SECOND / samplesPerSecond) * (clampedSampleIndex - i)
        const diffVal2 = square(dy) + square(dx)
        if (minDiffVal2 === undefined || diffVal2 < minDiffVal2) {
            minDiffVal2 = diffVal2
            minDiffIndex = i
        }
    }
    return {
        index: minDiffIndex,
        sampleData: samples[minDiffIndex],
    }
}
